"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { User, Hash } from "lucide-react";

const WAYS = [
  {
    icon: User,
    title: { en: "By name", es: "Por nombre" },
    detail: { en: "MARIA", es: "MARIA" },
    sub: { en: "Spelled out by the bartender. Common at small bars.", es: "Dictado por el bartender. Común en bares pequeños." },
  },
  {
    icon: Hash,
    title: { en: "By table", es: "Por mesa" },
    detail: { en: "TABLE 8", es: "MESA 8" },
    sub: { en: "Linked to a VIP table on your floor plan.", es: "Vinculada a una mesa VIP de tu floor plan." },
  },
];

export default function LiveBarTabsOpenScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#0F0B1F] text-white overflow-hidden">
      <div
        className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(124,58,237,0.15) 0%, transparent 65%)" }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#F472B6" }}>
            {t("Open a tab", "Abre una tab")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02]">
            {t("By name. ", "Por nombre. ")}
            <span className="text-white/30">{t("Or by table.", "O por mesa.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-white/55 leading-relaxed mt-6">
            {t(
              "Some customers give a name. Some sit at a VIP table you already booked. Either way, the tab opens the same way and shows up at every bar station.",
              "Algunos te dicen su nombre. Otros se sientan en la mesa VIP que ya reservaste. De cualquier forma, la tab se abre igual y aparece en cada estación de barra."
            )}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-[720px] mx-auto">
          {WAYS.map((way, i) => {
            const Icon = way.icon;
            return (
              <motion.div
                key={way.title.en}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: 0.1 * i, ease: [0.21, 0.47, 0.32, 0.98] }}
                className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-[20px] p-6"
              >
                <div
                  className="w-11 h-11 rounded-[12px] flex items-center justify-center mb-5"
                  style={{ backgroundColor: "rgba(124,58,237,0.2)" }}
                >
                  <Icon className="w-5 h-5" style={{ color: "#C4B5FD" }} />
                </div>
                <div className="text-[10px] font-black tracking-widest uppercase text-white/45 mb-2">
                  {t(way.title.en, way.title.es)}
                </div>
                <div className="text-[22px] font-black text-white mb-3 tabular-nums tracking-tight">
                  {t(way.detail.en, way.detail.es)}
                </div>
                <div className="text-[13px] font-medium text-white/55 leading-relaxed">
                  {t(way.sub.en, way.sub.es)}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
